import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  GitBranch,
  Plus,
  Edit,
  Trash2,
  Loader2,
  RefreshCw,
  Search,
  Star,
} from "lucide-react";
import type { PipelineRecord, PipelineStageRecord } from "@/types";

const EMPTY_PIPELINE_FORM = { name: "", code: "", description: "", is_default: false };

const EMPTY_STAGE_FORM = {
  name: "",
  order_index: 0,
  probability_percent: 10,
  is_won_stage: false,
  is_lost_stage: false,
  color_code: "#3b82f6",
  sla_days: "" as string | number,
};

/** Names of the standard textile stage types */
const STAGE_TYPE_LABELS: Record<string, string> = {
  ENQUIRY: "Enquiry",
  SAMPLING: "Sampling",
  QUOTATION: "Quotation",
  NEGOTIATION: "Negotiation",
  ORDER: "Order",
  WON: "Won",
  LOST: "Lost",
};

type DeleteTarget =
  | { kind: "pipeline"; item: PipelineRecord }
  | { kind: "stage"; item: PipelineStageRecord };

export function PipelinePage() {
  const [pipelines, setPipelines] = useState<PipelineRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Pipeline dialog: null = closed, "new" = add, record = edit
  const [pipelineDialog, setPipelineDialog] = useState<PipelineRecord | "new" | null>(null);
  const [pipelineForm, setPipelineForm] = useState(EMPTY_PIPELINE_FORM);

  // Stage dialog: tracks owning pipeline and (when editing) the stage
  const [stageDialog, setStageDialog] = useState<{
    pipeline: PipelineRecord;
    stage: PipelineStageRecord | null;
  } | null>(null);
  const [stageForm, setStageForm] = useState(EMPTY_STAGE_FORM);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { success, error } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/Pipeline/List");
      const list = unwrapArray<PipelineRecord>(res).map((p) => ({
        ...p,
        stages: [...(p.stages || [])].sort((a, b) => a.order_index - b.order_index),
      }));
      setPipelines(list);
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load pipelines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = pipelines.filter((p) => {
    const q = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q);
  });

  // ---------- Pipeline handlers ----------

  const openAddPipeline = () => {
    setPipelineForm(EMPTY_PIPELINE_FORM);
    setPipelineDialog("new");
  };

  const openEditPipeline = (p: PipelineRecord) => {
    setPipelineForm({
      name: p.name,
      code: p.code,
      description: p.description || "",
      is_default: p.is_default,
    });
    setPipelineDialog(p);
  };

  const handlePipelineSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pipelineForm.name.trim() || !pipelineForm.code.trim()) {
      error("Validation Error", "Name and code are required");
      return;
    }

    const payload = {
      name: pipelineForm.name.trim(),
      code: pipelineForm.code.trim().toUpperCase(),
      description: pipelineForm.description.trim() || undefined,
      is_default: pipelineForm.is_default,
    };

    setActionLoading(true);
    try {
      if (pipelineDialog === "new") {
        await api.post("/api/Pipeline/Insert", payload);
        success("Pipeline Created", "New sales pipeline created successfully");
      } else if (pipelineDialog) {
        await api.put(`/api/Pipeline/Update/${pipelineDialog.id}`, payload);
        success("Pipeline Updated", "Pipeline saved successfully");
      }
      setPipelineDialog(null);
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save pipeline");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetDefault = async (p: PipelineRecord) => {
    setActionLoading(true);
    try {
      await api.put(`/api/Pipeline/Update/${p.id}`, {
        name: p.name,
        code: p.code,
        is_default: true,
      });
      success("Default Pipeline Changed", `${p.name} is now the default pipeline`);
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to set default pipeline");
    } finally {
      setActionLoading(false);
    }
  };

  // ---------- Stage handlers ----------

  const openAddStage = (p: PipelineRecord) => {
    const nextOrder = p.stages.length
      ? Math.max(...p.stages.map((s) => s.order_index)) + 1
      : 1;
    setStageForm({ ...EMPTY_STAGE_FORM, order_index: nextOrder });
    setStageDialog({ pipeline: p, stage: null });
  };

  const openEditStage = (p: PipelineRecord, s: PipelineStageRecord) => {
    setStageForm({
      name: s.name,
      order_index: s.order_index,
      probability_percent: s.probability_percent,
      is_won_stage: s.is_won_stage,
      is_lost_stage: s.is_lost_stage,
      color_code: s.color_code || "#3b82f6",
      sla_days: s.sla_days ?? "",
    });
    setStageDialog({ pipeline: p, stage: s });
  };

  const handleStageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stageDialog) return;
    if (!stageForm.name.trim()) {
      error("Validation Error", "Stage name is required");
      return;
    }
    if (stageForm.is_won_stage && stageForm.is_lost_stage) {
      error("Validation Error", "A stage cannot be both Won and Lost");
      return;
    }
    if (stageForm.probability_percent < 0 || stageForm.probability_percent > 100) {
      error("Validation Error", "Probability must be between 0 and 100");
      return;
    }

    const payload = {
      pipeline_id: stageDialog.pipeline.id,
      name: stageForm.name.trim(),
      order_index: Number(stageForm.order_index),
      probability_percent: Number(stageForm.probability_percent),
      is_won_stage: stageForm.is_won_stage,
      is_lost_stage: stageForm.is_lost_stage,
      color_code: stageForm.color_code,
      sla_days: stageForm.sla_days === "" ? null : Number(stageForm.sla_days),
    };

    setActionLoading(true);
    try {
      if (stageDialog.stage) {
        await api.put(`/api/Pipeline/UpdateStage/${stageDialog.stage.id}`, payload);
        success("Stage Updated", "Pipeline stage saved successfully");
      } else {
        await api.post("/api/Pipeline/AddStage", payload);
        success("Stage Added", "New stage added to pipeline");
      }
      setStageDialog(null);
      await loadData();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save stage");
    } finally {
      setActionLoading(false);
    }
  };

  // ---------- Delete handler ----------

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      if (deleteTarget.kind === "pipeline") {
        await api.delete(`/api/Pipeline/Delete/${deleteTarget.item.id}`);
        success("Pipeline Deleted", "Pipeline removed successfully");
      } else {
        await api.delete(`/api/Pipeline/DeleteStage/${deleteTarget.item.id}`);
        success("Stage Deleted", "Stage removed successfully");
      }
      setDeleteTarget(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Sales Pipelines</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure deal pipelines, stage order, win probability and won/lost stages
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={openAddPipeline} className="h-9 gap-1.5 shadow-sm">
            <Plus className="size-4" />
            Add Pipeline
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          placeholder="Search pipelines by name or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-9 text-sm"
        />
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-card py-10 text-center text-sm text-muted-foreground">
          No pipelines configured.
        </div>
      ) : (
        <div className="space-y-5">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-border bg-card shadow-xs overflow-hidden"
            >
              {/* Pipeline header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/30">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="size-8 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                    <GitBranch className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-foreground">{p.name}</span>
                      <code className="px-2 py-0.5 rounded bg-muted font-mono text-[11px] font-semibold">
                        {p.code}
                      </code>
                      {p.is_default && <Badge variant="success">Default</Badge>}
                    </div>
                    {p.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {p.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!p.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(p)}
                      disabled={actionLoading}
                      className="h-8 gap-1.5 text-xs"
                      title="Make this the default pipeline"
                    >
                      <Star className="size-3.5" />
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAddStage(p)}
                    className="h-8 gap-1.5 text-xs"
                  >
                    <Plus className="size-3.5" />
                    Add Stage
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => openEditPipeline(p)}
                    title="Edit Pipeline"
                  >
                    <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setDeleteTarget({ kind: "pipeline", item: p })}
                    disabled={p.is_default}
                    title={p.is_default ? "The default pipeline cannot be deleted" : "Delete Pipeline"}
                  >
                    <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                  </Button>
                </div>
              </div>

              {/* Stages */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Order</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Probability</TableHead>
                    <TableHead>Time limit</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {p.stages.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {s.order_index}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span
                            className="size-3 rounded-full shrink-0 border border-border"
                            style={{ backgroundColor: s.color_code }}
                          />
                          <span className="font-medium text-sm text-foreground">{s.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{s.probability_percent}%</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.sla_days ? `${s.sla_days} days` : "—"}</TableCell>
                      <TableCell>
                        {s.stage_key && !s.is_won_stage && !s.is_lost_stage ? (
                          <Badge variant="blue" title="Standard stage: deals must meet its requirements to move on">
                            {STAGE_TYPE_LABELS[s.stage_key] || s.stage_key}
                          </Badge>
                        ) : s.is_won_stage ? (
                          <Badge variant="success">Won</Badge>
                        ) : s.is_lost_stage ? (
                          <Badge variant="destructive">Lost</Badge>
                        ) : (
                          <Badge variant="outline">Open</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => openEditStage(p, s)}
                          title="Edit Stage"
                        >
                          <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setDeleteTarget({ kind: "stage", item: s })}
                          title={s.stage_key ? "Standard stages can't be deleted" : "Delete Stage"}
                          disabled={!!s.stage_key}
                        >
                          <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {p.stages.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                        No stages yet. Add one to start using this pipeline.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: Add / Edit Pipeline */}
      <Dialog open={!!pipelineDialog} onOpenChange={(open) => !open && setPipelineDialog(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{pipelineDialog === "new" ? "Add Pipeline" : "Edit Pipeline"}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {pipelineDialog === "new"
                ? "Create a new sales pipeline. Add stages after saving."
                : "Update the pipeline name, description or default flag."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePipelineSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Pipeline Name *</Label>
              <Input
                required
                placeholder="Enterprise Sales"
                value={pipelineForm.name}
                onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Code *</Label>
              <Input
                required
                placeholder="ENTERPRISE_SALES"
                value={pipelineForm.code}
                disabled={pipelineDialog !== "new"}
                onChange={(e) => setPipelineForm({ ...pipelineForm, code: e.target.value })}
                className="font-mono uppercase"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Description</Label>
              <Input
                placeholder="Optional"
                value={pipelineForm.description}
                onChange={(e) => setPipelineForm({ ...pipelineForm, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3.5 py-3">
              <div>
                <Label className="text-xs sm:text-sm font-semibold">Default Pipeline</Label>
                <p className="text-[11px] sm:text-xs text-muted-foreground">New deals use the default pipeline</p>
              </div>
              <Switch
                checked={pipelineForm.is_default}
                disabled={pipelineDialog !== "new" && pipelineDialog?.is_default}
                onCheckedChange={(checked) =>
                  setPipelineForm({ ...pipelineForm, is_default: checked })
                }
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setPipelineDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Pipeline
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Add / Edit Stage */}
      <Dialog open={!!stageDialog} onOpenChange={(open) => !open && setStageDialog(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{stageDialog?.stage ? "Edit Stage" : "Add Stage"}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Pipeline: <strong className="text-foreground">{stageDialog?.pipeline.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStageSubmit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Stage Name *</Label>
              <Input
                required
                placeholder="Order Packed"
                value={stageForm.name}
                onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Order</Label>
                <Input
                  type="number"
                  min={0}
                  value={stageForm.order_index}
                  onChange={(e) =>
                    setStageForm({ ...stageForm, order_index: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Probability %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={stageForm.probability_percent}
                  onChange={(e) =>
                    setStageForm({ ...stageForm, probability_percent: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Time limit (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  placeholder="No limit"
                  value={stageForm.sla_days}
                  onChange={(e) => setStageForm({ ...stageForm, sla_days: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Color</Label>
                <Input
                  type="color"
                  value={stageForm.color_code}
                  onChange={(e) => setStageForm({ ...stageForm, color_code: e.target.value })}
                  className="p-1 h-10 sm:h-11 rounded-lg cursor-pointer w-full"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="flex items-center justify-between rounded-lg border border-border px-3.5 py-3">
                <Label className="text-xs sm:text-sm font-semibold">Won Stage</Label>
                <Switch
                  disabled={!!stageDialog?.stage?.stage_key}
                  checked={stageForm.is_won_stage}
                  onCheckedChange={(checked) =>
                    setStageForm({
                      ...stageForm,
                      is_won_stage: checked,
                      is_lost_stage: checked ? false : stageForm.is_lost_stage,
                    })
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border px-3.5 py-3">
                <Label className="text-xs sm:text-sm font-semibold">Lost Stage</Label>
                <Switch
                  disabled={!!stageDialog?.stage?.stage_key}
                  checked={stageForm.is_lost_stage}
                  onCheckedChange={(checked) =>
                    setStageForm({
                      ...stageForm,
                      is_lost_stage: checked,
                      is_won_stage: checked ? false : stageForm.is_won_stage,
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setStageDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Stage
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600">
              Delete {deleteTarget?.kind === "pipeline" ? "Pipeline" : "Stage"}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to remove{" "}
              <strong className="text-foreground">{deleteTarget?.item.name}</strong>?
              {deleteTarget?.kind === "pipeline"
                ? " All of its stages will be removed as well."
                : " Stages that still contain deals cannot be deleted."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
